"use client";

import { useMemo } from 'react'
import { shellLinkClick } from '@/lib/clientNav'
import { useFinance, latestMonth } from '@/lib/finance/finance-data-context'
import { fmtDate, fmtUsd } from '@/lib/finance/format'
import { aggregateAccounts } from '@/lib/finance/display'
import { balanceAsOf, balanceSeries } from '@/lib/finance/asof'
import { ACCOUNT_GROUPS, accountGroupColor, accountGroupOf, accountGroupId } from '@/lib/finance/categories'
import { PageHeader, Sparkline, Money } from '@/components/finance/ui'
import { usePageRange } from '@/components/finance/RangePicker'
import { downsample } from '@/components/finance/chartDefaults'
import { isStale, stalenessDays } from '@/lib/finance/gather'

export function Accounts() {
  const { accounts: rawAccounts, networth } = useFinance()
  // The two calculated 401k halves collapse into one combined line here; the
  // Net Worth page keeps them split for the Roth-vs-non-Roth chart.
  const accounts = useMemo(() => aggregateAccounts(rawAccounts), [rawAccounts])
  const last = latestMonth(networth.months)
  const { startMonth, control } = usePageRange(networth.months[0], last)
  // Change is measured over the selected window (start-of-window → latest).
  const priorMonth = networth.months.find((m) => m >= startMonth) ?? networth.months[0]

  // Sparklines are 120px wide — thin the window's dates to ~1 point/px so 68
  // rows don't each carry a thousand-segment SVG path on wide ranges.
  const sparkMonths = useMemo(
    () => downsample(networth.months.filter((m) => m >= startMonth), undefined, 120),
    [networth.months, startMonth],
  )

  const groups = useMemo(() => {
    const now = new Date()
    return ACCOUNT_GROUPS.map((group) => {
      // Show every active account (open/tracked) — including $0 paid-off cards
      // and accounts still awaiting their first balance (shown at $0). Closed/
      // emptied accounts (active === false) are hidden here but still count
      // toward historical net worth. `active` is treated as true when absent,
      // so cached data from before the flag existed still renders.
      const members = accounts
        .filter((a) => accountGroupOf(a.type) === group && a.active !== false)
        .map((a) => ({
          account: a,
          balance: balanceAsOf(a, last) ?? 0,
          // Staleness uses the per-account freshness policy in lib/finance/
          // gather.ts — the same one the Import console flags with — so the
          // two screens can't disagree about what "stale" means.
          stale: isStale(a, now),
          // Sparkline spans the whole window (null before the account opened),
          // so it aligns on the time axis with the other rows. One sweep, not
          // a per-month balanceAsOf key scan.
          spark: balanceSeries(a, sparkMonths),
        }))
        .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))
      const total = members.reduce((s, m) => s + (m.balance ?? 0), 0)
      return { group, members, total }
    }).filter((g) => g.members.length > 0)
  }, [accounts, last, sparkMonths])

  const staleAccounts = useMemo(
    () => groups.flatMap((g) => g.members).filter((m) => m.stale),
    [groups],
  )

  return (
    <div className="space-y-4">
      <PageHeader title="Accounts">{control}</PageHeader>

      {staleAccounts.length > 0 && (
        <div className="rounded-xl border border-warn/30 bg-warn/10 px-4 py-2.5 text-sm text-warn">
          <span className="font-semibold">
            {staleAccounts.length} account{staleAccounts.length === 1 ? '' : 's'} need{staleAccounts.length === 1 ? 's' : ''} a refresh
          </span>
          <span className="text-warn/80">
            {' — '}
            {staleAccounts.slice(0, 4).map((m) => m.account.name).join(', ')}
            {staleAccounts.length > 4 && ` +${staleAccounts.length - 4} more`}
          </span>
        </div>
      )}

      {groups.map(({ group, members, total }) => (
        <section
          key={group}
          id={accountGroupId(group)}
          className="scroll-mt-20 overflow-hidden rounded-2xl border border-edge bg-surface-1"
        >
          {/* Prominent group header — colored accent bar, filled band, and a
              hard divider so it reads as a section head, not another row. */}
          <header
            className="flex items-center justify-between gap-3 border-b border-edge bg-surface-3/60 px-4 py-2.5"
            style={{ boxShadow: `inset 3px 0 0 ${accountGroupColor(group)}` }}
          >
            <h2 className="flex items-center gap-2 text-sm font-bold tracking-tight text-ink">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: accountGroupColor(group) }}
                aria-hidden
              />
              {group}
              <span className="text-xs font-normal text-ink-3">
                {members.length} account{members.length === 1 ? '' : 's'}
              </span>
            </h2>
            <span className="text-base font-bold tabular-nums text-ink">{fmtUsd(total)}</span>
          </header>
          <ul className="divide-y divide-edge px-2 py-1">
            {members.map(({ account, balance, stale, spark }) => {
              // Delta over the window. If the account didn't exist at the window
              // start (no balance then), compare against 0 so a mid-range open
              // still shows its full growth.
              const then = balanceAsOf(account, priorMonth) ?? 0
              const sparkDefined = spark.filter((v) => v != null).length
              return (
                <li key={account.id}>
                  <a
                    onClick={shellLinkClick}
                    href={`/finance/accounts/${encodeURIComponent(account.id)}`}
                    className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-surface-3/50"
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: accountGroupColor(group) }}
                        aria-hidden
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium">{account.name}</span>
                          {stale && (
                            <span className="shrink-0 rounded-full bg-warn/15 px-1.5 py-0.5 text-[10px] font-semibold text-warn">
                              Needs refresh
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-xs text-ink-3">
                          {account.institution} · {account.owner}
                        </span>
                        <span
                          className={`block truncate text-[11px] ${stale ? 'text-warn' : 'text-ink-3'}`}
                        >
                          {account.asOf ? `Updated ${fmtDate(account.asOf)}` : 'Never updated'}
                          {stale && ` · stale >${stalenessDays(account)}d`}
                        </span>
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-4">
                      {sparkDefined > 1 && <Sparkline data={spark} className="hidden sm:block" />}
                      {/* Fixed-width so the sparkline column lines up row to row */}
                      <span className="w-24 shrink-0 text-right">
                        <span className="block text-sm font-semibold tabular-nums">{fmtUsd(balance!)}</span>
                        {balance !== then && (
                          <span className="block text-xs">
                            <Money value={balance! - then} compact />{' '}
                            <span className="text-ink-3">chg</span>
                          </span>
                        )}
                      </span>
                    </span>
                  </a>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}
